import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../components/ui';
import {
  FinalResponseConfirmation, FinalResponsePreview, confirmFinalResponse, previewFinalResponse,
} from '../../api/cafCorpus';
import { pushLayer } from '../../components/curation/overlayStack';
import { newIdempotencyKey } from '../../components/curation/saveEngine';
import './lanes.css';

/**
 * Lender task lists, Final response list (spec 2026-10-08 §4.12, R10): staff
 * confirm or reject the AI's final-response call. It uses the existing
 * adjudication endpoints (admin_caf_corpus.py:1807): preview, then confirm
 * with the preview's evidence and one idempotency key. After the save it
 * offers the teach form, pre-filled by the caller with the same setting.
 *
 * A lost reply may still have saved. The window then keeps the exact same
 * request (same key) and retries it: the server replays a repeat key
 * instead of recording a second judgment.
 */

export interface FinalResponseCheckModalProps {
  taskId: string;
  subject: string | null;
  /** true = "Confirm final response"; false = "Not a final response". */
  isFinalResponse: boolean;
  onClose: () => void;
  /** The server confirmed the judgment (called once). */
  onSaved: (isFinalResponse: boolean) => void;
  /** Staff chose to teach the AI from this email. */
  onTeach: (isFinalResponse: boolean) => void;
}

type Step = 'reason' | 'check' | 'saved';

const DEFAULT_REASON = {
  yes: 'I read the letter. It is a final response.',
  no: 'I read the letter. It is not a final response.',
};

const NOT_PERMITTED = 'Only admins and curators can change final responses.';

const valueWords = (value: boolean | null): string => (value === null ? 'Not recorded' : value ? 'Yes' : 'No');

/** Final review C7: a permission refusal is always said in plain words (the server's
 * text there is a code with spaces). Only the adjudication's plain 400 and 409
 * answers ("This email changed since preview...") are shown as the server wrote them. */
const refusalWords = (status: number | null, message: string | null, fallback: string): string => {
  if (status === 403) return NOT_PERMITTED;
  if ((status === 400 || status === 409) && message) return message;
  return fallback;
};

const errorOf = (error: unknown): { status: number | null; message: string | null } => {
  const response = (error as { response?: { status?: unknown; data?: { error?: unknown } } })?.response;
  return {
    status: typeof response?.status === 'number' ? response.status : null,
    message: typeof response?.data?.error === 'string' ? response.data.error : null,
  };
};

/** The server read the request and said no. Anything else -- no reply, 5xx,
 * timeout, rate limit -- may have saved. */
const isDefiniteRefusal = (status: number | null): boolean =>
  status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429;

export const FinalResponseCheckModal: React.FC<FinalResponseCheckModalProps> = ({
  taskId, subject, isFinalResponse, onClose, onSaved, onTeach,
}) => {
  const [step, setStep] = useState<Step>('reason');
  const [reason, setReason] = useState(isFinalResponse ? DEFAULT_REASON.yes : DEFAULT_REASON.no);
  const [preview, setPreview] = useState<FinalResponsePreview | null>(null);
  const [request, setRequest] = useState<FinalResponseConfirmation | null>(null);
  const [busy, setBusy] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [problem, setProblem] = useState('');
  // State updates are async: a ref stops a fast double click sending twice.
  const inFlight = useRef(false);
  const title = isFinalResponse ? 'Confirm final response' : 'Not a final response';
  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= 3 && trimmed.length <= 500;
  const canClose = !busy && !unconfirmed;
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Final review C6. The curation overlay manager (overlayStack): Escape from
  // anywhere on the page (but not while a request is running or a save is
  // unconfirmed), Tab kept inside the window, the lane behind it inert, and
  // focus back to the button that opened it when the window goes. The window
  // renders outside the app root (a portal), as curation's own Modal does, so
  // only the lane is inert. Declared before the focus effect below so it
  // records the opener, not the reason field, as where focus goes back to.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const blockedRef = useRef(!canClose);
  blockedRef.current = !canClose;
  useEffect(() => {
    if (!dialogRef.current) return undefined;
    return pushLayer(dialogRef.current, reasonRef.current, () => closeRef.current(), () => blockedRef.current);
  }, []);
  // The field and the button that had focus unmount with each step, so focus
  // moves with the step and never falls out of the window.
  useEffect(() => { (step === 'reason' ? reasonRef.current : dialogRef.current)?.focus(); }, [step]);

  const check = async () => {
    if (!reasonOk || busy || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setProblem('');
    try {
      const result = await previewFinalResponse(taskId, { has_final_response: isFinalResponse, reason: trimmed });
      setPreview(result);
      setRequest({
        has_final_response: result.after,
        reason: result.reason,
        expected_storage: result.expected_storage,
        expected_row_version: result.expected_row_version,
        expected_source_hash: result.expected_source_hash,
        expected_protected_hash: result.expected_protected_hash,
        idempotency_key: newIdempotencyKey('lane-final-response'),
      });
      setStep('check');
    } catch (error) {
      const { status, message } = errorOf(error);
      setProblem(refusalWords(status, message, "Couldn't check this email. Try again."));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!request || busy || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setProblem('');
    try {
      await confirmFinalResponse(taskId, request);
      setUnconfirmed(false);
      setStep('saved');
      onSaved(isFinalResponse);
    } catch (error) {
      const { status, message } = errorOf(error);
      if (isDefiniteRefusal(status)) {
        // The server checks the key before staleness, so any refusal means the first try did not save.
        setUnconfirmed(false);
        // Nothing was saved (e.g. the email changed since the check): start again from a fresh check.
        setPreview(null);
        setRequest(null);
        setStep('reason');
        setProblem(refusalWords(status, message, "Couldn't save. Check the email again."));
      } else {
        setUnconfirmed(true);
        setProblem("Couldn't confirm it saved. Try again: it will not be recorded twice.");
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return createPortal(
    <div className="lane-modal-backdrop" onClick={() => { if (canClose) onClose(); }}>
      <div
        ref={dialogRef}
        className="lane-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fr-check-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="fr-check-title" className="lane-modal__title">{step === 'saved' ? 'Saved' : title}</h2>
        {subject && step !== 'saved' && <p className="lane-modal__hint">{subject}</p>}
        {problem && <div className="lane-modal__error" role="alert">{problem}</div>}

        {step === 'reason' && (
          <>
            <p className="lane-modal__hint">
              {isFinalResponse
                ? 'This records that the email is a final response, so the FOS deadline applies.'
                : 'This records that the email is not a final response.'}
            </p>
            <div className="lane-modal__field">
              <label htmlFor="fr-check-reason">Why?</label>
              <textarea
                id="fr-check-reason"
                ref={reasonRef}
                className="lane-modal__input"
                rows={3}
                maxLength={500}
                value={reason}
                disabled={busy}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
            <div className="lane-modal__actions">
              <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={() => { void check(); }} disabled={busy || !reasonOk}>
                {busy ? 'Checking…' : 'Next'}
              </Button>
            </div>
          </>
        )}

        {step === 'check' && preview && (
          <>
            <p>Final response: <strong>{valueWords(preview.before)} → {valueWords(preview.after)}</strong></p>
            <p className="lane-modal__hint">Reason: {preview.reason}</p>
            <div className="lane-modal__actions">
              {!unconfirmed && (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => { setStep('reason'); setPreview(null); setRequest(null); setProblem(''); }}
                >
                  Back
                </Button>
              )}
              <Button variant="primary" onClick={() => { void confirm(); }} disabled={busy}>
                {busy ? 'Saving…' : unconfirmed ? 'Try again' : 'Confirm'}
              </Button>
            </div>
          </>
        )}

        {step === 'saved' && (
          <>
            <p>{isFinalResponse ? 'Recorded as a final response.' : 'Recorded as not a final response.'}</p>
            <p className="lane-modal__hint">
              Teach the AI from this email? It is saved as an example marked
              {isFinalResponse ? ' "Final response".' : ' "Not a final response".'}
            </p>
            <div className="lane-modal__actions">
              <Button variant="secondary" onClick={onClose}>Not now</Button>
              <Button variant="primary" onClick={() => onTeach(isFinalResponse)}>Teach the AI</Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default FinalResponseCheckModal;
